import { Dispatch } from 'react-redux';
import { StoreState } from '../../reducers';
import { Action } from '../../actions';
import Utils, { thumbnailsFolder, downloadsFolder, iconsFolder, defaultFolderIconFolder, bgMediaFolder } from '../../utils';
import { Api } from '../../typings/api';
import UserAgentUtils from '../../utils/userAgent';
import DriveItemUtils from '../../utils/driveItem';
import NativeApi, { DownloadPriority } from '../../utils/nativeApi';
import UrlUtils from '../../utils/url';
import { DownloadStatus, OfflineItemStatus, LogoThumbState, ThumbType, BrandThumbConfigLocation, LogoType, DefaultFolderIconState } from './reducer';
import {
	getAllDriveItems,
	receiveDriveItems,
	receiveResolutions,
	getOrFetchDriveItemMeta,
	receiveTemplateItems,
	updateFolderTree,
} from '../DriveItemList/actions';
import { DriveItemInfo } from '../DriveItemList/reducer';
import LocalStorageManager from '../../utils/localStorage/manager';
import { requestMobileAppDownload, deleteItem, deletePendingDownloads, downloadImportantAssets } from '../DownloadManager/actions';
import { DownloadManagerItemState } from '../DownloadManager/reducer';
import { DownloadMode } from '../DownloadManager/reducer';
import { MenuItemType } from '../../typings/menu';
import { createNotification } from '../NotificationBar/actions';
import { updateNewItemsStoreOffline } from '../DriveItemList/New/actions';
import ApiUtils from '../../utils/api';
import { checkActiveSession } from '../MultiAppAuthentication/actions';
import { getInitialDependenciesUpdates } from '../DownloadManager/actions/download';

const Q = require('q');

enum OfflineManagerActionKeys {
	INIT_THUMB_MAP = '@@offlineManager/INIT_THUMB_MAP',
	INIT_REQUIRED_ITEMS = '@@offlineManager/INIT_REQUIRED_ITEMS',
	INIT_LAST_UPDATE_TIMESTAMP = '@@offlineManager/INIT_LAST_UPDATE_TIMESTAMP',
	INIT_BRAND_THUMB = '@@offlineManager/INIT_BRAND_THUMB',
	INIT_HOME_THUMB = '@@offlineManager/INIT_HOME_THUMB',
	INIT_BG_MEDIA_MAP = '@@offlineManager/INIT_BG_MEDIA_MAP',
	INIT_DEFAULT_FOLDER_ICON = '@@offlineManager/INIT_DEFAULT_FOLDER_ICON',
	START_INITIALIZATION = '@@offlineManager/START_INITIALIZATION',
	END_INITIALIZATION = '@@offlineManager/END_INITIALIZATION',
	INITIALIZATION_ERROR = '@@offlineManager/INITIALIZATION_ERROR',
	UPDATE_THUMB_STATUS = '@@offlineManager/UPDATE_THUMB_STATUS',
	UPDATE_REQUIRED_ITEM_STATUS = '@@offlineManager/UPDATE_REQUIRED_ITEM_STATUS',
	UPDATE_BRAND_THUMB = '@@offlineManager/UPDATE_BRAND_THUMB',
	UPDATE_FOLDER_BG_MEDIA_STATUS = '@@offlineManager/UPDATE_FOLDER_BG_MEDIA_STATUS',
	UPDATE_DEFAULT_FOLDER_ICON = '@@offlineManager/UPDATE_DEFAULT_FOLDER_ICON',
	REMOVE_REQUIRED_ITEM = '@@offlineManager/REMOVE_REQUIRED_ITEM',
	REMOVE_FOLDER_BG_MEDIA = '@@offlineManager/REMOVE_FOLDER_BG_MEDIA',
	REMOVE_THUMB = '@@offlineManager/REMOVE_THUMB',
	UPDATE_DOWNLOAD_THUMBS_STATUS = '@@offlineManager//UPDATE_DOWNLOAD_THUMBS_STATUS',
	UPDATE_DOWNLOAD_REQUIRED_ITEM_STATUS = '@@offlineManager//UPDATE_DOWNLOAD_REQUIRED_ITEM_STATUS'
}

interface InitThumbMapAction extends Action {
	type: OfflineManagerActionKeys.INIT_THUMB_MAP;
	thumbMap: { [k: number]: OfflineItemStatus };
}

interface InitRequiredItemsAction extends Action {
	type: OfflineManagerActionKeys.INIT_REQUIRED_ITEMS;
	requiredItems: { [k: number]: OfflineItemStatus };
}

interface InitLastUpdateTimestampAction extends Action {
	type: OfflineManagerActionKeys.INIT_LAST_UPDATE_TIMESTAMP;
	lastUpdateTimestamp: Date;
}

interface InitBrandThumbAction extends Action {
	type: OfflineManagerActionKeys.INIT_BRAND_THUMB;
	brandThumb: LogoThumbState;
}

interface InitHomeThumbAction extends Action {
	type: OfflineManagerActionKeys.INIT_HOME_THUMB;
	homeThumb: LogoThumbState;
}

interface InitBgMediaMapAction extends Action {
	type: OfflineManagerActionKeys.INIT_BG_MEDIA_MAP;
	bgMediaMap: { [k: number]: OfflineItemStatus };
}

interface InitDefaultFolderIconAction extends Action {
	type: OfflineManagerActionKeys.INIT_DEFAULT_FOLDER_ICON;
	defaultFolderIcon: DefaultFolderIconState;
}


interface StartInitializationAction extends Action {
	type: OfflineManagerActionKeys.START_INITIALIZATION;
}

interface EndInitializationAction extends Action {
	type: OfflineManagerActionKeys.END_INITIALIZATION;
}

interface InitializationErrorAction extends Action {
	type: OfflineManagerActionKeys.INITIALIZATION_ERROR;
}

interface UpdateThumbStatusAction extends Action {
	type: OfflineManagerActionKeys.UPDATE_THUMB_STATUS;
	driveItemId: number;
	version: number;
	status: DownloadStatus;
	fileName?: string;
}

interface UpdateRequiredItemStatusAction extends Action {
	type: OfflineManagerActionKeys.UPDATE_REQUIRED_ITEM_STATUS;
	version: number;
	id: number;
	isIcon: boolean;
	url?: string;
	status: DownloadStatus;
}

interface UpdateBrandThumbStatusAction extends Action {
	type: OfflineManagerActionKeys.UPDATE_BRAND_THUMB;
	status: DownloadStatus;
	configLocation: BrandThumbConfigLocation;
	thumbType: ThumbType;
	filename?: string;
}

interface UpdateFolderBgMediaStatusAction extends Action {
	type: OfflineManagerActionKeys.UPDATE_FOLDER_BG_MEDIA_STATUS;
	driveItemId: number;
	version: number;
	status: DownloadStatus;
}

interface UpdateDefaultFolderIconStatusAction extends Action {
	type: OfflineManagerActionKeys.UPDATE_DEFAULT_FOLDER_ICON;
	status: DownloadStatus;
	name?: string;
}

interface RemoveRequiredItemAction extends Action {
	type: OfflineManagerActionKeys.REMOVE_REQUIRED_ITEM;
	id: number;
}

interface RemoveFolderBgMediaAction extends Action {
	type: OfflineManagerActionKeys.REMOVE_FOLDER_BG_MEDIA;
	id: number;
}

interface RemoveThumbAction extends Action {
	type: OfflineManagerActionKeys.REMOVE_THUMB;
	id: number;
}

interface UpdateDownloadThumbsStatusAction extends Action {
	type: OfflineManagerActionKeys.UPDATE_DOWNLOAD_THUMBS_STATUS;
	isDownloading: boolean;
}

interface UpdateRequiredItemsStatusAction extends Action {
	type: OfflineManagerActionKeys.UPDATE_DOWNLOAD_REQUIRED_ITEM_STATUS;
	isDownloading: boolean;
}

type OfflineManagerAction = InitThumbMapAction
	| InitRequiredItemsAction
	| InitLastUpdateTimestampAction
	| InitBrandThumbAction
	| InitHomeThumbAction
	| InitBgMediaMapAction
	| InitDefaultFolderIconAction
	| StartInitializationAction
	| EndInitializationAction
	| InitializationErrorAction
	| UpdateThumbStatusAction
	| UpdateRequiredItemStatusAction
	| UpdateBrandThumbStatusAction
	| UpdateFolderBgMediaStatusAction
	| UpdateDefaultFolderIconStatusAction
	| RemoveRequiredItemAction
	| RemoveFolderBgMediaAction
	| RemoveThumbAction
	| UpdateDownloadThumbsStatusAction
	| UpdateRequiredItemsStatusAction;

const loadOfflineManager = () => (dispatch: Dispatch<StoreState>): void => {
	const previousOfflineManagerState = LocalStorageManager.getStoreOfflineManager();

	if (previousOfflineManagerState) {
		const previousThumbMap = previousOfflineManagerState.thumbMap || {};
		const previousRequiredItems = previousOfflineManagerState.requiredItems || {};
		const previousBgMediaMap = previousOfflineManagerState.bgMediaMap || {};

		let newThumbMap = {};
		let newRequiredItems = {};
		let newBgMediaMap = {};

		Object.keys(previousThumbMap).forEach(key => {
			const driveItemId = parseInt(key, 10);
			const previousThumbStatus: OfflineItemStatus = previousThumbMap[driveItemId];

			if (previousThumbStatus.status === DownloadStatus.DOWNLOADED) {
				newThumbMap[driveItemId] = previousThumbStatus;
			}
		});

		Object.keys(previousRequiredItems).forEach(key => {
			const driveItemId = parseInt(key, 10);
			const previousRequiredItemStatus: OfflineItemStatus = previousRequiredItems[driveItemId];

			if (previousRequiredItemStatus.status === DownloadStatus.DOWNLOADED) {
				newRequiredItems[driveItemId] = previousRequiredItemStatus;
			}
		});

		Object.keys(previousBgMediaMap).forEach(key => {
			const driveItemId = parseInt(key, 10);
			const previousBgMediaStatus: OfflineItemStatus = previousBgMediaMap[driveItemId];

			if (previousBgMediaStatus.status === DownloadStatus.DOWNLOADED) {
				newBgMediaMap[driveItemId] = previousBgMediaStatus;
			}
		});

		dispatch({
			type: OfflineManagerActionKeys.INIT_THUMB_MAP,
			thumbMap: newThumbMap
		});

		dispatch({
			type: OfflineManagerActionKeys.INIT_REQUIRED_ITEMS,
			requiredItems: newRequiredItems
		});

		dispatch({
			type: OfflineManagerActionKeys.INIT_BG_MEDIA_MAP,
			bgMediaMap: newBgMediaMap
		});

		if (previousOfflineManagerState.lastUpdateTimestamp) {
			dispatch({
				type: OfflineManagerActionKeys.INIT_LAST_UPDATE_TIMESTAMP,
				lastUpdateTimestamp: new Date(previousOfflineManagerState.lastUpdateTimestamp)
			});
		}

		if (previousOfflineManagerState.brandThumb) {
			dispatch({
				type: OfflineManagerActionKeys.INIT_BRAND_THUMB,
				brandThumb: previousOfflineManagerState.brandThumb,
			});
		}

		if (previousOfflineManagerState.defaultFolderIcon) {
			dispatch({
				type: OfflineManagerActionKeys.INIT_DEFAULT_FOLDER_ICON,
				defaultFolderIcon: previousOfflineManagerState.defaultFolderIcon,
			});
		}
	}
};

let updateFolderTreeInterval;
const initializeOfflineManager = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): {} => {
	dispatch(startInitialization());
	const {
		galleryInfos,
		galleryInfos: {
			template,
			config: {
				importantAssets,
				applicationGallery,
			},
		},
	} = getState();
	if (UserAgentUtils.isMobileApp) {
		if (UserAgentUtils.isOnline()) {
			const requiredItemsDatas = getRequiredItemsDatas(template);
			const shouldDownloadImportantAssets = importantAssets && importantAssets.enabled && importantAssets.isDownloadedOnLaunch;

			dispatch(loadDependenciesMetaFromLocalStorage());

			return dispatch(getAllDriveItems())
				.catch(() => {
					dispatch(createNotification('error.initialization_fail', { id: galleryInfos.config.applicationGallery }, -1));
					return dispatch(sendInitializationError());
				})
				.then(() => dispatch(getOrFetchDriveItemMeta(applicationGallery, undefined, undefined, true)))
				.then(() => dispatch({
					type: OfflineManagerActionKeys.UPDATE_DOWNLOAD_REQUIRED_ITEM_STATUS,
					isDownloading: true
				}))
				.then(() => NativeApi.existDirectory(downloadsFolder) as PromiseLike<boolean>)
				.then(directoryExist => directoryExist ? {} : NativeApi.createDirectory(downloadsFolder))
				.then(() => dispatch(downloadRequiredItems(requiredItemsDatas)))
				.then(() => {
					dispatch({
						type: OfflineManagerActionKeys.UPDATE_DOWNLOAD_REQUIRED_ITEM_STATUS,
						isDownloading: false
					});
					dispatch({
						type: OfflineManagerActionKeys.UPDATE_DOWNLOAD_THUMBS_STATUS,
						isDownloading: true
					});
				})
				.then(() => NativeApi.existDirectory(bgMediaFolder) as PromiseLike<boolean>)
				.then(directoryExist => directoryExist ? {} : NativeApi.createDirectory(bgMediaFolder))
				.then(() => dispatch(downloadAllBgMedia(galleryInfos.config.applicationGallery, 0, [])))
				.then(() => NativeApi.existDirectory(thumbnailsFolder) as PromiseLike<boolean>)
				.then(directoryExist => directoryExist ? {} : NativeApi.createDirectory(thumbnailsFolder))
				.then(() => dispatch(downloadBrandThumbIfNeeded(BrandThumbConfigLocation.REMOTE_CONFIG, template, false)))
				.then(() => dispatch(downloadDefaultFolderIcon()))
				.then(() => dispatch(downloadThumbs()))
				.then(() => dispatch(deleteObsoleteItems(requiredItemsDatas)))
				.then(() => dispatch(deleteObsoleteThumbsAndBgMedia()))
				.then(() => dispatch({
					type: OfflineManagerActionKeys.UPDATE_DOWNLOAD_THUMBS_STATUS,
					isDownloading: false
				}))
				.then(() => dispatch(endInitialization()))
				.then(() => dispatch(setUpdateTreeInterval()))
				.then(() => dispatch(getInitialDependenciesUpdates()))
				.then(() => shouldDownloadImportantAssets && dispatch(downloadImportantAssets()));
		} else {
			const cachedDriveListState = LocalStorageManager.getStoreDriveList();
			if (cachedDriveListState) {
				const { driveItems, resolutions } = cachedDriveListState;
				dispatch(receiveDriveItems(driveItems));
				dispatch(receiveResolutions(resolutions));
				dispatch(updateNewItemsStoreOffline());
				dispatch(deletePendingDownloads());
			} else {
				dispatch(createNotification('info.offline_no_config', { id: galleryInfos.config.applicationGallery }, -1));
				return dispatch(sendInitializationError());
			}
		}
	}
	return dispatch(endInitialization());
};


const setUpdateTreeInterval = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	updateFolderTreeInterval = setInterval(() => dispatch(updateFolderTreeIfNeeded()), 20 * 60 * 1000);
};

const clearUpdateTreeInterval = () => clearInterval(updateFolderTreeInterval);

const updateFolderTreeIfNeeded = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	const MAX_HOURS_BETWEEN_UPDATES = 4;

	const isUpdateNeeded = (lastUpdateDate: Date, currentDate: Date) => {
		const isLastUpdateNotToday = lastUpdateDate.toDateString() !== currentDate.toDateString();
		if (isLastUpdateNotToday) {
			return true;
		}

		const hoursSinceLastUpdate = currentDate.getHours() - lastUpdateDate.getHours();

		return hoursSinceLastUpdate > MAX_HOURS_BETWEEN_UPDATES;
	};

	const {
		driveList: {
			folderTreeUpdate: {
				lastDate
			},
		},
	} = getState();

	if (lastDate) {
		const lastUpdateDate = new Date(lastDate);
		const currentDate = new Date();
		if (isUpdateNeeded(lastUpdateDate, currentDate)) {
			dispatch(checkActiveSession())
				.then(() => dispatch(updateFolderTree(false)));
		}
	}
};

const loadDependenciesMetaFromLocalStorage = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	// When online, we don't reuse old saved store an fetch all items in gallery.
	// We have to get the dependencies of downloaded items that are not in gallery from the saved drive items
	const cachedDriveListState = LocalStorageManager.getStoreDriveList();
	if (cachedDriveListState) {
		const { downloadManager } = getState();

		let dependencies: number[] = [];
		Object.keys(downloadManager.items).forEach(id => {
			const downloadItem: DownloadManagerItemState = downloadManager.items[id];
			dependencies = [...dependencies, ...downloadItem.dependencies];
		});
		// Remove duplicate entries
		dependencies = dependencies.reduce((dependencies: number[], dependencyId: number) => dependencies.includes(dependencyId) ? dependencies : [...dependencies, dependencyId], []);

		const dependenciesMeta = {};
		dependencies.forEach(dependencyId => {
			const driveItem: DriveItemInfo = cachedDriveListState.driveItems[dependencyId];

			if (driveItem && !driveItem.isInGallery) {
				dependenciesMeta[dependencyId] = driveItem;
			}
		});

		dispatch(receiveDriveItems(dependenciesMeta));
	}
 };

const getObsoleteOfflineItems = (offlineItemsMap: { [k: string]: OfflineItemStatus }, currentData: RequiredItemData[]): number[] => {
	let obsoleteIds: number[] = [];

	Object.keys(offlineItemsMap).forEach(key => {
		const id = parseInt(key, 10);
		if (!currentData.find(data => id === data.id)) {
			obsoleteIds.push(id);
		}
	});

	return obsoleteIds;
};

const deleteObsoleteItems = (requiredItemsDatas: RequiredItemData[]) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<{}[]> => {
	const { offlineManager } = getState();
	const requiredItemsStatus = offlineManager.requiredItems;

	const obsoleteItemsIds = getObsoleteOfflineItems(requiredItemsStatus, requiredItemsDatas);

	let promises: Promise<{}>[] = obsoleteItemsIds.map(id =>
		dispatch(getOrFetchDriveItemMeta(id, false))
			.then((itemMeta: Api.DriveItem) => {
				if (requiredItemsStatus[id].isIcon) {
					return dispatch(deleteThumbOrIcon(itemMeta, true));
				} else {
					return dispatch(deleteItem(itemMeta))
						.then(() => dispatch(removeRequiredItem(id)));
				}
			})
		);

	return Q.allSettled(promises);
};

const downloadRequiredItems = (requiredItemsDatas: RequiredItemData[]) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<{}[]> => {
	const { offlineManager, driveList } = getState();
	const { driveItems } = driveList;
	const requiredItemsStatus = offlineManager.requiredItems;

	let promises: Promise<{}>[] = requiredItemsDatas.map(item =>
		dispatch(getOrFetchDriveItemMeta(item.id, false))
			.then((itemMeta: Api.DriveItem) => {
				const isOutdated = requiredItemsStatus[item.id] && (item.url !== requiredItemsStatus[item.id].url || itemMeta.version > requiredItemsStatus[item.id].version);
				const isDownloadNeeded = !requiredItemsStatus[item.id] || isOutdated;

				if (isDownloadNeeded) {
					if (item.isIcon) {
						return dispatch(downloadThumb(item.id, DownloadPriority.High, true));
					} else {
						return dispatch(requestMobileAppDownload(itemMeta, DownloadMode.Required))
							.then(() => dispatch(updateRequiredItemStatus(itemMeta, DownloadStatus.DOWNLOADED, false, item.url)))
							.catch(() => dispatch(updateRequiredItemStatus(itemMeta, DownloadStatus.ERROR, false, item.url)));
					}
				}
				return {};
			})
			.catch(() => {
				dispatch(updateRequiredItemStatus(driveItems[item.id], DownloadStatus.ERROR, item.isIcon, item.url));
				return Promise.reject({});
			})
	);

	return Q.allSettled(promises);
};

interface RequiredItemData {
	id: number;
	isIcon: boolean;
	url?: string;
}

const getRequiredItemsDatas = (template: Api.Template): RequiredItemData[] => {
	let requiredItems: RequiredItemData[] = [];

	if (template.menu) {
		const menuFormat = Utils.getScreenFormat();
		const menu = template.menu[menuFormat] || template.menu.default;

		const getItemId = (requiredData: string, isIcon: boolean) => {
			const mediaId = Utils.getMediaId(requiredData);
			if (mediaId > 0) {
				requiredItems.push({ id: mediaId, isIcon });
			}
		};

		if (template.welcomepage && template.welcomepage.url) {
			requiredItems.push({
				id: Utils.getMediaId(template.welcomepage.url),
				url: template.welcomepage.url,
				isIcon: false
			});
		}

		Object.keys(menu).forEach(menuPosition => {
			menu[menuPosition].forEach(menuItem => {
				if (menuItem.type === MenuItemType.Page) {
					getItemId(menuItem.target, false);
				}

				if (menuItem.icon) {
					getItemId(menuItem.icon, true);
				}
			});
		});
	}

	// filter out duplicates;
	return requiredItems.filter((page, index, self) => {
		return index === self.findIndex(selfPage => {
			return selfPage.id === page.id;
		});
	});
};

const downloadDirectChildrenThumb = (driveItemId: number, level: number, previousDriveItemIds: number[], isHighPriority: boolean = false) =>
	async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	const { driveList } = getState();
	const { resources } = driveList.driveItems[driveItemId];

	let currentLevelPromises: Q.Promise<{}>[] = [];
	if (resources) {
		currentLevelPromises = resources.map(resource => dispatch(handleDriveItemThumbnail(resource.id, isHighPriority)));
	}

	const childPromises: Promise<void>[] = [];
	if (resources) {
		resources.forEach(resource => {
			const isResourceWithChildren = resource.type === Api.DriveItemType.Directory || resource.type === Api.DriveItemType.Gallery;
			const shouldGoDeeper = isResourceWithChildren && !previousDriveItemIds.includes(resource.id);
			if (shouldGoDeeper) {
				childPromises.push(dispatch(downloadDirectChildrenThumb(resource.id, level + 1, [...previousDriveItemIds, driveItemId], false)));
			}
		});
	}

	await Q.allSettled(currentLevelPromises);
	if (level === 0) {
		return; // used so that initial call resolve after thumbs of first level are handled
	}

	await Q.allSettled(childPromises);
};

const handleDriveItemThumbnail = (driveItemId: number, isHighPriority: boolean = false) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<{}> => {
		const {
			driveList: {
				driveItems
			},
			offlineManager: {
				thumbMap
			}
		} = getState();

		const driveItem = driveItems[driveItemId];
		if (driveItem) {
			const driveItemStatus = thumbMap[driveItemId];
			const isOutdated = driveItemStatus && driveItem.version > driveItemStatus.version;
			const isDownloadNeeded = (!driveItemStatus || isOutdated) && DriveItemUtils.hasIcon(driveItem);
			const isRemoveNeeded = driveItemStatus && !DriveItemUtils.hasIcon(driveItem);

			const priority = isHighPriority ? DownloadPriority.High : DownloadPriority.Low;

			if (isDownloadNeeded) {
				return dispatch(downloadThumb(driveItemId, priority, false));
			}

			if (isRemoveNeeded) {
				return dispatch(removeThumb(driveItemId));
			}
		}

		return Q.Promise.resolve({});
	};

const downloadAllBgMedia = (driveItemId: number, level: number, previousDriveItemIds: number[]) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<{}> => {
	const {
		driveList,
		offlineManager,
		galleryInfos: { config: { applicationGallery } },
	} = getState();
	const { driveItems } = driveList;
	const { resources } = driveList.driveItems[driveItemId];
	const { bgMediaMap } = offlineManager;
	let promises: Q.Promise<{}>[] = [];

	const addToPromisesQueue = (driveItemId: number) => {
		const driveItem = driveItems[driveItemId];

		if (driveItem) {
			const driveItemStatus = bgMediaMap[driveItemId];
			const isOutdated = driveItemStatus && driveItem.version > driveItemStatus.version;
			const isDownloadNeeded = (!driveItemStatus || isOutdated) && driveItem.bgMedia;
			const isRemoveNeeded = driveItemStatus && !driveItem.bgMedia;

			if (isDownloadNeeded) {
				promises.push(dispatch(downloadFolderBgMedia(driveItemId, DownloadPriority.Normal)));
			}

			if (isRemoveNeeded) {
				promises.push(dispatch(removeFolderBgMedia(driveItemId)));
			}
		}
	};

	if (driveItemId === applicationGallery) {
		addToPromisesQueue(driveItemId);
	}

	if (resources) {
		resources.forEach(resource => addToPromisesQueue(resource.id));
	}

	return Q.allSettled(promises)
		.then(() => {
			let promises: Promise<{}>[] = [];
			if (resources) {
				resources.forEach(resource => {
					const isResourceWithChildren = resource.type === Api.DriveItemType.Directory || resource.type === Api.DriveItemType.Gallery;
					const shouldGoDeeper = isResourceWithChildren && !previousDriveItemIds.includes(resource.id);
					if (shouldGoDeeper) {
						promises.push(dispatch(downloadAllBgMedia(resource.id, level + 1, [...previousDriveItemIds, driveItemId])));
					}
				});
			}
			return level === 0 ? {} : Q.allSettled(promises);
		});
};

const downloadFolderBgMedia = (driveItemId: number, priority: DownloadPriority) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<{}> => {
	const { galleryInfos, driveList, offlineManager } = getState();
	const { config } = galleryInfos;
	const { driveItems } = driveList;
	const driveItem = driveItems[driveItemId];
	const { bgMediaMap } = offlineManager;
	const driveItemStatus = bgMediaMap[driveItemId];

	if (UserAgentUtils.isOnline()
		&& driveItem.bgMedia
		&& !(driveItemStatus && (driveItemStatus.status === DownloadStatus.DOWNLOADING || driveItemStatus.status === DownloadStatus.ERROR))
	) {
		const extension = Utils.getFileExtension(driveItem.bgMedia);
		const fileName = `background.${extension}`;
		const folderPath = `${bgMediaFolder}/${driveItemId}`;

		return NativeApi.existDirectory(folderPath)
			.then(directoryExist => directoryExist
				// is needed to avoid storing of several files with different extensions
				? NativeApi.deleteDirJs(`../../${folderPath}`)
				: {}
			)
			.then(() => NativeApi.downloadFile(
				config,
				UrlUtils.getUrlFromRelUrlWithMediaParam(driveItem.bgMedia as string, config),
				folderPath,
				fileName,
				priority
			).promise
			.then(() => {
				return dispatch(updateFolderBgMediaStatus(driveItemId, driveItem.version, DownloadStatus.DOWNLOADED));
			})
			.catch(() => {
				return dispatch(updateFolderBgMediaStatus(driveItemId, driveItem.version, DownloadStatus.ERROR));
			}));
	}

	return Q.Promise.reject();
};

const removeFolderBgMedia = (driveItemId: number) => (dispatch: Dispatch<StoreState>): Q.Promise<{}> => {
	const folderPath = `${bgMediaFolder}/${driveItemId}`;

	return NativeApi.existDirectory(folderPath)
		.then(directoryExist => directoryExist
			? NativeApi.deleteDirJs(`../../${folderPath}`)
			: {}
		)
		.then(() => dispatch({
			type: OfflineManagerActionKeys.REMOVE_FOLDER_BG_MEDIA,
			id: driveItemId,
		}));
};

const removeThumb = (driveItemId: number, shouldRemoveFromOfflineManager: boolean = true) => (dispatch: Dispatch<StoreState>): Q.Promise<{}> => {
	const folderPath = `${thumbnailsFolder}/${driveItemId}`;

	return NativeApi.existDirectory(folderPath)
		.then(directoryExist => directoryExist
			? NativeApi.deleteDirJs(`../../${folderPath}`)
			: {}
		)
		.then(() => shouldRemoveFromOfflineManager
			? dispatch({
				type: OfflineManagerActionKeys.REMOVE_THUMB,
				id: driveItemId,
			})
			: {}
		);
};

const downloadThumb = (
	driveItemId: number,
	priority: DownloadPriority,
	isIcon: boolean,
	driveItemMeta?: Api.DriveItem,
	forceVersion?: boolean,
) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<{}> => {
	const { galleryInfos, driveList, offlineManager } = getState();
	const { config } = galleryInfos;
	const { driveItems, resolutions } = driveList;
	const driveItem = driveItemMeta || driveItems[driveItemId];
	const { thumbMap } = offlineManager;
	const driveItemStatus = thumbMap[driveItemId];

	if (UserAgentUtils.isOnline()
		&& (DriveItemUtils.hasIcon(driveItem) || isIcon)
		&& !(driveItemStatus && (driveItemStatus.status === DownloadStatus.DOWNLOADING || driveItemStatus.status === DownloadStatus.ERROR))
	) {
		const thumbnailPath = isIcon ? DriveItemUtils.getIconOriginalPath(driveItem, config) : DriveItemUtils.getThumbnail(driveItem, config, resolutions, true);
		const extension = Utils.getFileExtension(thumbnailPath);
		const folder = isIcon ? iconsFolder : `${thumbnailsFolder}/${driveItemId}`;
		const fileName = isIcon ? `${driveItemId}.${extension}` : `card_thumbnail.${extension}`;
		// In the case where the download of a thumb silently failed, forceVersion is here to make sure the redownloaded thumb is being loaded and not a cached version of a missing file
		const thumbnailVersion = forceVersion ?
			-Math.floor(Math.random() * 99999) :
			driveItem.version;

		return NativeApi.downloadFile(config, thumbnailPath, folder, fileName, priority)
			.promise
			.then(() => {
				return dispatch(isIcon
					? updateRequiredItemStatus(driveItem, DownloadStatus.DOWNLOADED, true, undefined, thumbnailVersion)
					: updateThumbStatus(driveItemId, thumbnailVersion, DownloadStatus.DOWNLOADED, fileName));
			})
			.catch(() => {
				dispatch(deleteThumbOrIcon(driveItem, isIcon));
				return dispatch(isIcon
					? updateRequiredItemStatus(driveItem, DownloadStatus.ERROR, true, undefined, thumbnailVersion)
					: updateThumbStatus(driveItemId, thumbnailVersion, DownloadStatus.ERROR));
			});
	}

	return Q.Promise.reject();
};

const deleteThumbOrIcon = (driveItem: Api.DriveItem, isIcon: boolean) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<{}> => {
	const { galleryInfos, driveList } = getState();
	const { resolutions } = driveList;

	const thumbnailPath = DriveItemUtils.getThumbnail(driveItem, galleryInfos.config, resolutions, isIcon ? false : true);
	const extension = Utils.getFileExtension(thumbnailPath);
	const path = isIcon ? `${iconsFolder}/${driveItem.id}.${extension}` : `${thumbnailsFolder}/${driveItem.id}`;

	return isIcon
		? NativeApi.existFile(path).then(fileExist => fileExist ? NativeApi.removeFile(path) : {})
		: NativeApi.existDirectory(path).then(fileExist => fileExist ? NativeApi.deleteDirJs(`../../${path}`) : {});
	};

const downloadBrandThumbIfNeeded = (configLocation: BrandThumbConfigLocation, template: Api.Template, isHome: boolean) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<{}> => {
		if (UserAgentUtils.isMobileApp && UserAgentUtils.isOnline()) {
			const { galleryInfos, offlineManager, appInfos } = getState();
			const thumbType = isHome ? ThumbType.HOME_THUMB : ThumbType.BRAND_THUMB;
			const logoType = isHome ? LogoType.HOME_LOGO : LogoType.BRAND_LOGO;
			const logoName = Utils.getBrandLogoName(template, logoType);
			const isMultiApp = Utils.isMultiApp(appInfos);

			const { status, filename } = offlineManager[thumbType][configLocation];

			if (logoName && (filename !== logoName || status !== DownloadStatus.DOWNLOADED)) {
				const newFilename = Utils.getFilenameFromMediaId(logoName);
				const deletePromise = filename ? Q.Promise.resolve() : NativeApi.removeFile(`${thumbnailsFolder}/brand/${filename}`);
				dispatch(updateBrandThumbStatus(configLocation, thumbType, DownloadStatus.DOWNLOADING, logoName));

				return deletePromise
					.then(() => NativeApi.downloadFile(
						galleryInfos.config,
						Utils.getOriginBrandLogoUrl(galleryInfos, isHome, isMultiApp, template),
						`${thumbnailsFolder}/brand`,
						`${newFilename}`,
						DownloadPriority.High
					).promise)
					.then(() => { dispatch(updateBrandThumbStatus(configLocation, thumbType, DownloadStatus.DOWNLOADED, logoName)); })
					.catch(() => { dispatch(updateBrandThumbStatus(configLocation, thumbType, DownloadStatus.ERROR, logoName)); });
			}
		}
		return Q.Promise.resolve();
	};

const downloadDefaultFolderIcon = () =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<{}> => {
		if (UserAgentUtils.isMobileApp && UserAgentUtils.isOnline()) {
			const {
				galleryInfos: { config },
				offlineManager: { defaultFolderIcon: { status, name } },
			} = getState();
			const defaultFolderIcon = DriveItemUtils.getDefaultFolderIcon();

			if (!defaultFolderIcon) {
				const hasPrevDefaultFolderIcon = name && status === DownloadStatus.DOWNLOADED;
				if (hasPrevDefaultFolderIcon) {
					NativeApi.removeFile(`${thumbnailsFolder}/${defaultFolderIconFolder}/${name}`)
						.then(() => {
							dispatch(updateDefaultFolderIconStatus(DownloadStatus.NOT_DOWNLOADED));
						})
						.catch(() => {
							dispatch(updateDefaultFolderIconStatus(DownloadStatus.ERROR));
						});
				}

				return Q.Promise.resolve();
			}

			const newName = defaultFolderIcon.replace(UrlUtils.mediaParamRegex, '');

			if (name !== newName || status !== DownloadStatus.DOWNLOADED) {
				const deletePromise = name
					? NativeApi.removeFile(`${thumbnailsFolder}/${defaultFolderIconFolder}/${name}`)
					: Q.Promise.resolve();
				dispatch(updateDefaultFolderIconStatus(DownloadStatus.DOWNLOADING, newName));

				return deletePromise
					.then(() => NativeApi.downloadFile(
						config,
						UrlUtils.getUrlFromRelUrlWithMediaParam(defaultFolderIcon, config),
						`${thumbnailsFolder}/${defaultFolderIconFolder}`,
						newName,
						DownloadPriority.Normal
					).promise)
					.then(() => {
						dispatch(updateDefaultFolderIconStatus(DownloadStatus.DOWNLOADED, newName));
					})
					.catch(() => {
						dispatch(updateDefaultFolderIconStatus(DownloadStatus.ERROR, newName));
					});
			}
		}

		return Q.Promise.resolve();
	};

const downloadThumbs = () => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	dispatch(downloadTemplateThumbs());
	dispatch(downloadSharedFolderThumbs());
	await dispatch(downloadAppDirThumbs()); // we want to wait for the thumbnails of the root of the gallery
};

const downloadAppDirThumbs = () => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	const { galleryInfos } = getState();
	await dispatch(downloadDirectChildrenThumb(galleryInfos.config.applicationGallery, 0, [], true));
};

const downloadTemplateThumbs = () => async (dispatch: Dispatch<StoreState>): Promise<{}> => {

	const templatesData = await ApiUtils.getPublicationTemplates();
	const templateIds = [
		...templatesData.masterProposals.map(({ id }) => id),
		...templatesData.customTemplates.map(({ id }) => id),
	];

	// There are cases where assets in template are not in sharedDir nor app folders but meta is needed to get the thumbnail
	await dispatch(receiveTemplateItems(templatesData));

	const templateThumbsPromises = templateIds
		.map((id) => dispatch(handleDriveItemThumbnail(id)));
	return await Q.allSettled(templateThumbsPromises);
};

const downloadSharedFolderThumbs = () => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	const { galleryInfos: { config: { applicationGallery } } } = getState();
	await dispatch(getOrFetchDriveItemMeta(applicationGallery));
	const { driveList, galleryInfos: { config } } = getState();

	const sharedDirId = Utils.getSharedDirId(driveList, config);

	if (!sharedDirId) {
		return;
	}

	await dispatch(downloadDirectChildrenThumb(sharedDirId, 0, []));
};

const deleteObsoleteThumbsAndBgMedia = () =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<{}> => {
	const {
		driveList: { driveItems },
		offlineManager: { thumbMap, bgMediaMap },
	} = getState();
	let promises: Q.Promise<{}>[] = [];

	Object.keys(bgMediaMap).forEach((id) => {
		if (!driveItems[id]) {
			promises.push(dispatch(removeFolderBgMedia(+id)));
		}
	});

	Object.keys(thumbMap).forEach((id) => {
		if (!driveItems[id]) {
			promises.push(dispatch(removeThumb(+id)));
		}
	});

	return Q.allSettled(promises);
};

const updateBrandThumbStatus = (configLocation: BrandThumbConfigLocation, thumbType: ThumbType, status: DownloadStatus, filename?: string): UpdateBrandThumbStatusAction => ({
	type: OfflineManagerActionKeys.UPDATE_BRAND_THUMB,
	configLocation,
	thumbType,
	status,
	filename
});

const updateDefaultFolderIconStatus = (status: DownloadStatus, name?: string): UpdateDefaultFolderIconStatusAction => ({
	type: OfflineManagerActionKeys.UPDATE_DEFAULT_FOLDER_ICON,
	status,
	name,
});

const startInitialization = (): StartInitializationAction => ({ type: OfflineManagerActionKeys.START_INITIALIZATION });

const endInitialization = (): EndInitializationAction => ({ type: OfflineManagerActionKeys.END_INITIALIZATION });

const updateThumbStatus = (driveItemId: number, version: number, status: DownloadStatus, fileName?: string): UpdateThumbStatusAction => ({
	type: OfflineManagerActionKeys.UPDATE_THUMB_STATUS,
	driveItemId,
	version,
	status,
	fileName,
});

const updateRequiredItemStatus = (item: Api.DriveItem, status: DownloadStatus, isIcon: boolean, url?: string, forcedVersion?: number): UpdateRequiredItemStatusAction => ({
	type: OfflineManagerActionKeys.UPDATE_REQUIRED_ITEM_STATUS,
	version: forcedVersion || item.version,
	id: item.id,
	status,
	isIcon,
	url
});

const updateFolderBgMediaStatus = (driveItemId: number, version: number, status: DownloadStatus): UpdateFolderBgMediaStatusAction => ({
	type: OfflineManagerActionKeys.UPDATE_FOLDER_BG_MEDIA_STATUS,
	driveItemId,
	version,
	status
});

const removeRequiredItem = (id: number): RemoveRequiredItemAction => ({
	type: OfflineManagerActionKeys.REMOVE_REQUIRED_ITEM,
	id
});

const sendInitializationError = (): InitializationErrorAction => ({ type: OfflineManagerActionKeys.INITIALIZATION_ERROR });

export {
	OfflineManagerActionKeys, OfflineManagerAction, loadOfflineManager, initializeOfflineManager, clearUpdateTreeInterval,
	downloadThumb, downloadBrandThumbIfNeeded, updateBrandThumbStatus, updateThumbStatus, sendInitializationError, removeThumb,
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/OfflineManager/actions.ts